import { toMinute } from "@utils/date-time";

export type MovementTimes = {
  eta?: Date | null;
  etd?: Date | null;
  ets?: Date | null;
  etc?: Date | null;
  pilotUnderway?: Date | null;
  permissionToSail?: Date | null;
  anchorTime?: Date | null;
  anchorAweighTime?: Date | null;
  pob?: Date | null;
  firstLineAshore?: Date | null;
  allFast?: Date | null;
  pilotDisembark?: Date | null;
  ats?: Date | null;
  allLinesClear?: Date | null;
  allClear?: Date | null;
  pilotStationCode?: string | null;
};

export type MovementTimeField = keyof MovementTimes;

const PAST_OR_PRESENT_FIELDS = new Set<MovementTimeField>([
  "pilotUnderway",
  "permissionToSail",
  "anchorTime",
  "anchorAweighTime",
  "pob",
  "firstLineAshore",
  "allFast",
  "pilotDisembark",
  "ats",
  "allClear",
]);
const PRESENT_OR_FUTURE_FIELDS = new Set<MovementTimeField>(["eta", "etd", "ets", "etc"]);

export const getMovementTimeDateConstraint = (field: MovementTimeField) => {
  if (PAST_OR_PRESENT_FIELDS.has(field)) return "not-future" as const;
  if (PRESENT_OR_FUTURE_FIELDS.has(field)) return "not-past" as const;
  return undefined;
};

type ValidationOptions = {
  values: MovementTimes;
  visibleFields: ReadonlyArray<MovementTimeField>;
  getLabel: (field: MovementTimeField | "atc") => string;
  t: (key: string, options: Record<string, string>) => string;
  atc?: Date | null;
  earliestResourceStartTime?: Date | null;
  /** The update drawer checks date constraints only on fields the user edited. */
  dateConstraintFields?: ReadonlyArray<MovementTimeField>;
  now?: Date;
};

export const validateMovementTimes = ({
  values,
  visibleFields,
  getLabel,
  t,
  atc,
  earliestResourceStartTime,
  dateConstraintFields = visibleFields,
  now = new Date(),
}: ValidationOptions) => {
  const errors: Partial<Record<MovementTimeField, string>> = {};
  const currentMinute = toMinute(now)!;
  const isValidDate = (value: unknown): value is Date =>
    value instanceof Date && !isNaN(value.getTime());

  visibleFields.forEach((field) => {
    const value = values[field];
    if (field === "pilotStationCode" ? !value : !isValidDate(value)) {
      errors[field] = t("form-validation.required", { label: getLabel(field) });
      return;
    }
    if (!dateConstraintFields.includes(field) || !isValidDate(value)) return;
    const minute = toMinute(value)!;
    const constraint = getMovementTimeDateConstraint(field);
    if (constraint === "not-future" && minute > currentMinute) {
      errors[field] = t("form-validation.must-not-be-in-future", { label: getLabel(field) });
    }
    if (constraint === "not-past" && minute < currentMinute) {
      errors[field] = t("form-validation.must-not-be-in-past", { label: getLabel(field) });
    }
  });

  const orderRules: ReadonlyArray<readonly [MovementTimeField, MovementTimeField]> = [
    ["ets", "etc"],
    ["firstLineAshore", "allFast"],
    ["pilotUnderway", "pob"],
    ["pob", "pilotDisembark"],
    ["allFast", "pilotDisembark"],
    ["allClear", "allFast"],
    ["allClear", "anchorTime"],
    ["anchorAweighTime", "anchorTime"],
  ];
  orderRules.forEach(([before, after]) => {
    const first = values[before];
    const second = values[after];
    if (
      visibleFields.includes(before) &&
      visibleFields.includes(after) &&
      isValidDate(first) &&
      isValidDate(second) &&
      first > second
    ) {
      errors[after] = t("form-validation.must-be-after", {
        subject: getLabel(after),
        object: getLabel(before),
      });
    }
  });

  if (visibleFields.includes("ats") && isValidDate(values.ats)) {
    if (isValidDate(atc) && values.ats > atc) {
      errors.ats = t("form-validation.must-be-equal-or-before-value", {
        label: getLabel("ats"),
        value: getLabel("atc"),
      });
    }
    const atsMinute = toMinute(values.ats)!;
    const resourceMinute = toMinute(earliestResourceStartTime);
    if (resourceMinute && atsMinute > resourceMinute) {
      errors.ats = t("form-validation.ats-before-earliest-resource-start", {
        label: getLabel("ats"),
      });
    }
  }
  return errors;
};
